/**
 * @graphql cc
 */
import { graphql } from 'react-relay';
import gql from 'graphql-tag';
import { useCallback, useState, useEffect, useRef } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import type {
	useContentTypesTitleUpdateContentTitleMutation as useContentTypesTitleUpdateContentTitleMutation$data,
	useContentTypesTitleUpdateContentTitleMutationVariables as useContentTypesTitleUpdateContentTitleMutation$variables,
} from '@confluence/content-types-utils/entry-points/useContentTypesTitleUpdateContentTitleMutation.ts';
import { contentTypesTitleServiceSingleton } from '@confluence/content-types-header-base';

import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';
import { fg } from '@confluence/feature-gating';

import type { useContentTypesTitleUpdateContentTitleWriteFragment$data } from './__generated__/useContentTypesTitleUpdateContentTitleWriteFragment.graphql.ts';
import useContentTypesTitleUpdateContentTitleWriteFragmentArtifact from './__generated__/useContentTypesTitleUpdateContentTitleWriteFragment.graphql.ts';

type TitleUpdateData = {
	isEditing: boolean;
	setIsEditing: (boolean: boolean) => void;
	setUpdatedTitleFromPubsub: (value: string | undefined) => void;
	updateLoading: boolean;
	updateError?: ApolloErrorInterop;
	updateTitle: (value: string) => void;
	updateCache: (value: string) => void;
};

export const useContentTypesTitleUpdate = (
	contentId: string,
	currentTitle: string,
): TitleUpdateData => {
	const [isEditing, setIsEditing] = useState(false);
	const [updatedTitleFromPubsub, setUpdatedTitleFromPubsub] = useState<string | undefined>(
		undefined,
	);
	const isProcessingTitleUpdate = useRef(false);
	const onMutationComplete = () => {
		setUpdateError(undefined);
		setIsEditing(false);
	};
	const [updateError, setUpdateError] = useState<ApolloErrorInterop | undefined>(undefined);
	const [updateContentTitle, { loading: updateLoading }] = useMutation<
		useContentTypesTitleUpdateContentTitleMutation$data,
		useContentTypesTitleUpdateContentTitleMutation$variables
	>(
		gql`
			mutation useContentTypesTitleUpdateContentTitleMutation($contentId: ID!, $title: String) {
				updateTitle(contentId: $contentId, title: $title) {
					id
					title
					links {
						editui
						webui
					}
				}
			}
		`,
		{
			onCompleted: onMutationComplete,
			onError: setUpdateError,
		},
	);
	const writeFragmentInterop =
		useWriteFragmentInterop<useContentTypesTitleUpdateContentTitleWriteFragment$data>({
			fragmentArtifact: useContentTypesTitleUpdateContentTitleWriteFragmentArtifact,
			fragmentSpec: graphql`
				fragment useContentTypesTitleUpdateContentTitleWriteFragment on Content {
					title
					id
					__typename
				}
			`,
		});

	const updateCache = useCallback(
		(value: string) => {
			writeFragmentInterop({
				fragmentId: `Content:${contentId}`,
				data: {
					id: contentId,
					title: value,
					__typename: 'Content',
				},
			});
		},
		[contentId, writeFragmentInterop],
	);

	const updateTitle = useCallback(
		async (value: string) => {
			if (value === currentTitle) {
				setIsEditing(false);
				// We have to manually clear the error here because it is no longer relevant to this most recent call to `updateTitle`,
				// since we're now attempting to set the title back to the current title, which should be valid.
				setUpdateError(undefined);
				if (updatedTitleFromPubsub && updatedTitleFromPubsub !== currentTitle) {
					updateCache(updatedTitleFromPubsub);
					setUpdatedTitleFromPubsub(undefined);
				}
				if (!isProcessingTitleUpdate.current) {
					return;
				}
			}

			try {
				await updateContentTitle({
					variables: {
						contentId,
						title: value,
					},
				});
			} catch {
				// error handled by useMutation result updateError
			}
		},
		[contentId, currentTitle, updateCache, updateContentTitle, updatedTitleFromPubsub],
	);

	/**
	 * The following section is a mutation queue to ensure that title updates are processed sequentially and that the
	 * latest title is always the one that gets applied.
	 * This is important because multiple title updates can be triggered in quick succession when AI inline streaming
	 * occurs and we want to avoid stale state errors from the GQL store, but also ensure the latest title is the one that
	 * gets applied.
	 */
	const latestCachedTitle = useRef<string | undefined>();

	const processQueue = useCallback(async () => {
		if (isProcessingTitleUpdate.current) {
			return;
		}

		isProcessingTitleUpdate.current = true;

		let nextTitle = latestCachedTitle.current;
		latestCachedTitle.current = undefined;

		// We keep updating the title until there are no more titles in the cache
		while (!!nextTitle) {
			await updateTitle(nextTitle);
			nextTitle = latestCachedTitle.current;
			latestCachedTitle.current = undefined;
		}

		isProcessingTitleUpdate.current = false;
	}, [updateTitle]);

	const enqueueTitle = useCallback(
		async (title) => {
			latestCachedTitle.current = title;
			if (!isProcessingTitleUpdate.current) {
				await processQueue();
			}
		},
		[processQueue],
	);

	// Set the title change handler when the component mounts
	// Content types use this method to update the title from within the iframe
	useEffect(() => {
		contentTypesTitleServiceSingleton.setTitleChangeHandler(updateTitle);
		return () => {
			contentTypesTitleServiceSingleton.setTitleChangeHandler(() => {});
		};
	}, [updateTitle]);

	return {
		isEditing,
		setIsEditing,
		setUpdatedTitleFromPubsub,
		updateLoading,
		updateError,
		updateTitle: fg('platform_editor_ai_aifc_patch_ga_blockers') ? enqueueTitle : updateTitle,
		updateCache,
	};
};
